import {
  LanguageOutlined,
  MonitorHeartOutlined,
  PersonAddOutlined,
  SensorsOutlined,
  VideocamOutlined,
} from '@mui/icons-material';
import MoreIcon from '@mui/icons-material/More';
import { Box, CircularProgress } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { FuelTransitionIcon } from '@/components/FuelIcons';
import type { NavigationOptions } from '@/models/Ou';

import { Permission } from '../../../enums';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
// import AssetEditor from '../../../pages/assets/asset-editor/asset-editor';
import AssetDetailsModal from '../../../pages/assets/asset-details-modal/AssetDetailsModal';
import { LocalStorageService } from '../../../services/local-storage';
import { applicationDataSelector } from '../../../store/slices/application-data';
import { fuelUserAccessSliceSelector } from '../../../store/slices/fuelUserAccess';
import { SaveAssetJobsSelector } from '../../../store/slices/saveAssetJobsState/SaveAssetJobsState';
import { cacheUtils } from '../../../utils/cacheUtils';
import ChatWidget from '../../ChatWidget';
import IngestUploadProgressPopup from '../IngestUploadProgressPopup';

type Props = {
  preview?: boolean;
  availableOptions?: NavigationOptions;
};

export const Sidebar = ({ preview, availableOptions }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { ouData } = useSelector(applicationDataSelector);
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const [activeRoute, setActiveRoute] = useState('/assets/list');
  const [openAssetIngestDialg, setOpenAssetIngestDialg] = useState(false);
  const flags = useFlags();
  const { height } = useWindowDimensions();
  const [showMoreIcon, setShowMoreIcon] = useState(false);

  const { ingestInProgress } = useSelector(SaveAssetJobsSelector);
  const ingestUploadProgressRef = useRef();

  useEffect(() => {
    // console.log('location changed', location);
    if (flags['enable-view-nexa-new-ui-design'] === false) {
      resolveActiveRoute(null, location.pathname);
    }
  }, [location]);

  const resolveActiveRoute = (_: unknown, to: string) => {
    console.info('old sidebar resolveActiveRoute', { to });

    // if the RELOAD_ON_NAVIGATION flag is set, then immediately reload
    const reloadOnNavigation = LocalStorageService.getItem('RELOAD_ON_NAVIGATION');
    const enableUsePopupForIngest = flags['use-ingest-popup-for-upload-assets'];
    if (reloadOnNavigation) {
      LocalStorageService.removeItem('RELOAD_ON_NAVIGATION');
      cacheUtils.doHardRefresh();
      return;
    }

    if (to === '/' || to === '/assets') {
      // set default pages for unresolved routes
      navigate('/assets/list');
    }
    if (to === '/channelmanager' || to === '/channelmanager') {
      navigate('/channelmanager/list');
    }
    if (to === '/assets/new' && enableUsePopupForIngest) {
      navigate('/assets/list');
    }
    setActiveRoute(to);
  };

  const navClick = async (e: MouseEvent, to: string) => {
    if (preview) {
      e.preventDefault();
      return;
    }

    const enableUsePopupForIngest = flags['use-ingest-popup-for-upload-assets'];
    if (to === '/assets/new') {
      if (enableUsePopupForIngest) {
        setOpenAssetIngestDialg(true);
      } else {
        navigate('/assets/new');
      }
    } else {
      navigate(to);
    }
    e.stopPropagation();
  };

  const openIngestProgressPopup = () => {
    if (ingestUploadProgressRef && ingestUploadProgressRef.current) {
      ingestUploadProgressRef.current.open();
    }
  };

  useEffect(() => {
    // console.log('height', height);
    if (height < 600) {
      setShowMoreIcon(true);
    } else {
      setShowMoreIcon(false);
    }
  }, [height]);

  return (
    <div className={`sidebar-wrapper ${preview ? 'preview' : ''}`}>
      <div className="sidebar-background" />
      <div className="sidebar-nav-container">
        <ul>
          {permissions && permissions.has(Permission.Ingest) && (!availableOptions || availableOptions.uploadAssets) && (
            <Tooltip title="Upload Assets" placement="right" arrow>
              <li className={activeRoute === '/assets/new' ? 'active-route' : ''} onClick={(e) => navClick(e, '/assets/new')}>
                <span className="ion-ios-cloud-upload" />
                {/* <span>
                  <img src={uploadAsset} alt="Upload Assets" />
                </span> */}
              </li>
            </Tooltip>
          )}
          {permissions &&
            permissions.has(Permission.ListSearchAssets) &&
            (!availableOptions || availableOptions.assetLibrary) && (
              <Tooltip title="Asset Library" placement="right" arrow>
                <li className={activeRoute === '/assets/list' ? 'active-route' : ''} onClick={(e) => navClick(e, '/assets/list')}>
                  <span className="icon-stack">
                    <i className="fa fa-folder icon-stack-3x" />
                    <i className="fa fa-search icon-stack-1x" />
                  </span>
                  {/* <span>
                    <img src={assetLibrary} alt="Asset Library" />
                  </span> */}
                </li>
              </Tooltip>
            )}
          {/* {permissions && permissions.has(Permission.ChannelManager) && (
              <li
                slot="activator"
                className={_.startsWith(activeRoute, '/channelmanager') ? 'active-route' : ''}
                onClick={(e) => navClick(e, '/channelmanager/list')}
              >
                <span className="ion-android-options"></span>
                <ul className="channel-sub-menu">
                  {permissions && permissions.has(Permission.ListSearchChannels) && (
                    <li
                      className={_.startsWith(activeRoute, '/channelmanager') ? 'active-route' : ''}
                      onClick={(e) => navClick(e, '/channelmanager/list')}
                    >
                      <div>Channels</div>
                    </li>
                  )}
                  {permissions && permissions.has(Permission.ListSearchLivePrograms) && (
                    <li
                      className={_.startsWith(activeRoute, '/channelmanager/liveprograms') ? 'active-route' : ''}
                      onClick={(e) => navClick(e, '/channelmanager/liveprograms/list')}
                    >
                      <div>Live Programs</div>
                    </li>
                  )}
                  {permissions && permissions.has(Permission.ListSearchTransitionTemplates) && (
                    <li
                      className={_.startsWith(activeRoute, '/channelmanager/transitiontemplates') ? 'active-route' : ''}
                      onClick={(e) => navClick(e, '/channelmanager/transitiontemplates/list')}
                    >
                      <div>Transition Templates</div>
                    </li>
                  )}
                </ul>
              </li>
            )} */}
          {permissions &&
            permissions.has(Permission.ListSearchChannels) &&
            (!availableOptions || availableOptions.channelManager) && (
              <Tooltip title="Channel Manager" placement="right" arrow>
                <li
                  className={activeRoute === '/channelmanager/list' ? 'active-route' : ''}
                  onClick={(e) => navClick(e, '/channelmanager/list')}
                >
                  <span className="ion-android-options" />
                  {/* <span>
                    <img src={channelManager} alt="Channel Manager" />
                  </span> */}
                </li>
              </Tooltip>
            )}
          {permissions &&
            permissions.has(Permission.ListSearchLivePrograms) &&
            (!availableOptions || availableOptions.inputs) &&
            flags['input-page-ui-feature-enabled'] && (
              <Tooltip title="Inputs" placement="right" arrow>
                <li
                  className={activeRoute === '/live/inputs/list' ? 'active-route' : ''}
                  onClick={(e) => navClick(e, '/live/inputs/list')}
                >
                  <span>
                    <VideocamOutlined />
                    {/* <img src={inputs} alt="Inputs" /> */}
                  </span>
                </li>
              </Tooltip>
            )}
          {permissions &&
            permissions.has(Permission.ListSearchLivePrograms) &&
            (!availableOptions || availableOptions.livePrograms) && (
              <Tooltip title="Live Programs" placement="right" arrow>
                <li
                  className={activeRoute === '/live/programs/list' ? 'active-route' : ''}
                  onClick={(e) => navClick(e, '/live/programs/list')}
                >
                  <span>
                    <SensorsOutlined />
                    {/* <img src={liveProgram} alt="Live Program" /> */}
                  </span>
                </li>
              </Tooltip>
            )}
          {permissions &&
            permissions.has(Permission.ListSearchTransitionTemplates) &&
            (!availableOptions || availableOptions.transitionPackages) && (
              <Tooltip title="Transition Packages" placement="right" arrow>
                <li
                  className={activeRoute === '/transitiontemplates/list' ? 'active-route' : ''}
                  onClick={(e) => navClick(e, '/transitiontemplates/list')}
                >
                  <span>
                    <FuelTransitionIcon />
                    {/* <img src={transitionPackages} alt="Transition Packages" /> */}
                  </span>
                </li>
              </Tooltip>
            )}
          {permissions &&
            ouData &&
            // (permissions.has(Permission.OUSuperAdmin) || permissions.has(Permission.OUAdmin)) &&
            (!availableOptions || availableOptions.globalInterstitials) && (
              <Tooltip title="Global Interstitials" placement="right" arrow>
                <li
                  className={activeRoute === `/channelmanager/editor/${ouData.ouId}` ? 'active-route' : ''}
                  onClick={(e) => navClick(e, `/channelmanager/editor/${ouData.ouId}`)}
                >
                  <span>
                    <LanguageOutlined />
                    {/* <img src={globalInterstitials} alt="Global Interstitials" /> */}
                  </span>
                </li>
              </Tooltip>
            )}

          {!showMoreIcon && (
            <>
              {ouData &&
                permissions &&
                permissions.has(Permission.Dashboard) &&
                flags['enable-monitoring-dashboard-flag'] &&
                (!availableOptions || availableOptions.monitoringDashboard) && (
                  <Tooltip title="Monitoring Dashboard" placement="right" arrow>
                    <li
                      className={activeRoute === `/dashboards/stream-monitoring` ? 'active-route' : ''}
                      onClick={(e) => navClick(e, `/dashboards/stream-monitoring`)}
                    >
                      <span>
                        <MonitorHeartOutlined />
                        {/* <img src={monitoringDashboard} alt="Monitoring Dashboard" /> */}
                      </span>
                    </li>
                  </Tooltip>
                )}

              {permissions &&
                permissions.has(Permission.AnalyticsDashboard) &&
                (!availableOptions || availableOptions.analytics) && (
                  <Tooltip title="Analytics Dashboard" placement="right" arrow>
                    <li
                      className={activeRoute === '/dashboards/analytics' ? 'active-route' : ''}
                      onClick={(e) => navClick(e, '/dashboards/analytics')}
                    >
                      <span className="fa fa-bar-chart" />
                      {/* <span>
                        <img src={analytics} alt="Analytics Dashboard" />
                      </span> */}
                    </li>
                  </Tooltip>
                )}
              {permissions &&
                permissions.has(Permission.AdManagement) &&
                flags['fuel-ui-show-self-service-ads'] &&
                (!availableOptions || availableOptions.monetization) && (
                  <Tooltip title="Ad Tag Management" placement="right" arrow>
                    <li className={activeRoute === '/ads' ? 'active-route' : ''} onClick={(e) => navClick(e, '/ads')}>
                      {/* <span>
                        <img src={adTag} alt="Ad Tag Management" />
                      </span> */}
                      <span className="fa fa-dollar" />
                    </li>
                  </Tooltip>
                )}
              {permissions &&
                permissions.has(Permission.OUSuperAdmin) &&
                (!availableOptions || availableOptions['settings-admin']) && (
                  // (!availableOptions || availableOptions['settings-admin']) && ( // always show
                  <Tooltip title="Account Admin" placement="right" arrow>
                    <li
                      className={activeRoute === '/legacy-admin' ? 'active-route' : ''}
                      onClick={(e) => navClick(e, '/legacy-admin')}
                    >
                      {/* <span>
                        <img src={admin} alt="Admin" />
                      </span> */}
                      <span className="fa fa-cog" />
                    </li>
                  </Tooltip>
                )}
              {permissions && permissions.has(Permission.OUAdmin) && (!availableOptions || availableOptions.account) && (
                <Tooltip title="User Management" placement="right" arrow>
                  <li
                    className={activeRoute === '/account/cms-user-management' ? 'active-route' : ''}
                    onClick={(e) => navClick(e, '/account/cms-user-management')}
                  >
                    {/* <span>
                      <img src={cmsUser} alt="User Management" />
                    </span> */}
                    <span className="icon">
                      <PersonAddOutlined />
                    </span>
                  </li>
                </Tooltip>
              )}
              {/* {permissions && ouData && permissions.has(Permission.OUSuperAdmin) && (
                  <Tooltip title="UI Components" placement="right" arrow>
                    <li
                      className={activeRoute === `/ui-components` ? 'active-route' : ''}
                      onClick={(e) => navClick(e, `/ui-components`)}
                    >
                      <span>
                        <AutoStoriesIcon />
                      </span>
                    </li>
                  </Tooltip>
                )} */}
            </>
          )}
          {showMoreIcon && permissions && permissions.has(Permission.OUAdmin) && (
            <li slot="activator" title="More">
              <span>
                <MoreIcon sx={{ transform: 'rotate(-180deg)' }} />
                {/* <i className='ion-android-more-horizontal'></i> */}
              </span>

              <ul className="channel-sub-menu">
                {ouData &&
                  permissions &&
                  permissions.has(Permission.Dashboard) &&
                  flags['enable-monitoring-dashboard-flag'] &&
                  (!availableOptions || availableOptions.monitoringDashboard) && (
                    <li
                      className={activeRoute === '/dashboards/stream-monitoring' ? 'active-route' : ''}
                      onClick={(e) => navClick(e, '/dashboards/stream-monitoring')}
                    >
                      {/* <div></div> */}
                      <div>Monitoring Dashboard</div>
                    </li>
                  )}
                {permissions &&
                  permissions.has(Permission.AnalyticsDashboard) &&
                  (!availableOptions || availableOptions.analytics) && (
                    <li
                      className={activeRoute === '/dashboards/analytics' ? 'active-route' : ''}
                      onClick={(e) => navClick(e, '/dashboards/analytics')}
                    >
                      {/* <div></div> */}
                      <div>Analytics Dashboard</div>
                    </li>
                  )}
                {permissions &&
                  permissions.has(Permission.AdManagement) &&
                  flags['fuel-ui-show-self-service-ads'] &&
                  (!availableOptions || availableOptions.monetization) && (
                    <li className={activeRoute === '/ads' ? 'active-route' : ''} onClick={(e) => navClick(e, '/ads')}>
                      {/* <span className="fa fa-dollar"></span> */}
                      <div>Ad Tag Management</div>
                    </li>
                  )}
                {permissions &&
                  permissions.has(Permission.OUSuperAdmin) &&
                  (!availableOptions || availableOptions['settings-admin']) && (
                    <li
                      className={activeRoute === '/legacy-admin' ? 'active-route' : ''}
                      onClick={(e) => navClick(e, '/legacy-admin')}
                    >
                      <div>Account Admin</div>
                    </li>
                  )}
                {permissions && permissions.has(Permission.OUAdmin) && (!availableOptions || availableOptions.account) && (
                  <li
                    className={activeRoute === '/account/cms-user-management' ? 'active-route' : ''}
                    onClick={(e) => navClick(e, '/account/cms-user-management')}
                  >
                    <div>User Management</div>
                  </li>
                )}
              </ul>
            </li>
          )}
          {/* <li className={activeRoute === '/test' ? 'active-route' : ''} onClick={(e) => navClick(e, '/test')}>
                  <span className='demo-text'>Test</span>
                </li> */}

          <div className="bottomfloat">
            {ingestInProgress && (
              <Box sx={{ m: 1, position: 'relative' }} onClick={openIngestProgressPopup}>
                <Box sx={{ marginLeft: '14px' }}>
                  {/* <span> {ingestInProgress && <UploadIcon sx={{ color: '#fff' }} />}</span> */}
                </Box>
                <CircularProgress
                  size={25}
                  thickness={6}
                  sx={{
                    color: 'white',
                    position: 'absolute',
                    top: -40,
                    left: 15,
                    zIndex: 1,
                  }}
                />
              </Box>
            )}
            <IngestUploadProgressPopup ref={ingestUploadProgressRef} />

            <div className="help-link">
              {permissions &&
                permissions.has(Permission.Chatbot) &&
                flags['view-nexa-support-chatbot'] &&
                (!availableOptions || availableOptions.viewNexaAssistant) && <ChatWidget />}
              {(!availableOptions || availableOptions.help) && (
                <li slot="activator" className="">
                  {/* <span>
                    <img src={help} alt="help" />
                  </span> */}
                  <span className="material-icons">help</span>
                  <ul className="help-sub-menu">
                    <li className="">
                      <a href="https://help.bitcentral.com/help/viewnexa-articles" target="_blank" rel="noreferrer">
                        Help site-articles
                      </a>
                    </li>
                    <li className="">
                      <a href="https://help.bitcentral.com/help/viewnexa-learn-bitcentral" target="_blank" rel="noreferrer">
                        Video tutorials
                      </a>
                    </li>
                  </ul>
                </li>
              )}
            </div>
            <span className="viewnexa-logo" />
          </div>
        </ul>
        {openAssetIngestDialg && (
          <AssetDetailsModal open={openAssetIngestDialg} onClose={() => setOpenAssetIngestDialg(false)} isNew />
        )}
      </div>
    </div>
  );
};
